<template>
  <basic-dialog :show="show" @close="$emit('close')" max-width="900">
    <template v-slot:title>Supplier Notes</template>
    <v-row>
      <v-col
        ><v-select
          :items="suppliers"
          multiple
          label="Supplier(s)"
          dense
          hide-details
          item-text="name"
          item-value="id"
          :outlined="$store.getters.localSettings('outlinedInputs')"
          clearable
          v-model="selectedSuppliersFilter"
      /></v-col>
      <v-col
        ><v-select
          :items="notes"
          multiple
          label="Note(s)"
          dense
          hide-details
          item-text="name"
          item-value="id"
          :outlined="$store.getters.localSettings('outlinedInputs')"
          clearable
          v-model="selectedNotesFilter"
      /></v-col>
    </v-row>
    <v-row
      ><v-col>
        <v-data-table
          :headers="headers"
          :items="items"
          :loading="$apollo.loading"
          item-key="id"
          mobile-break-point="1264"
          class="elevation-1"
          show-expand
        >
          <template v-slot:item.actions="{ item }">
            <default-list-actions
              :item="item"
              :hideView="!readOnly"
              @editItem="onEditItem"
              @deleteItem="onDeleteItem"
              :hideEdit="readOnly"
              :hideRemove="readOnly"
              @viewItem="onViewItem"
            ></default-list-actions>
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length">
              <v-container fluid>
                <p class="content" v-html="item.content"></p>
              </v-container>
            </td>
          </template>
        </v-data-table> </v-col
    ></v-row>
    <supplier-iti-note-edit-dialog
      :show="showItemEditor"
      v-model="editedItem"
      :suppliers="suppliers"
      @close="showItemEditor = false"
      @input="onSaveItem"
      :read-only="readOnly"
    />
    <template v-slot:actions>
      <v-btn color="primary" v-if="!readOnly" @click="onAddNote"
        >Add Note</v-btn
      >
    </template>
    <v-row v-if="debug">
      <v-col
        ><code>{{ items }}</code
        ><code>{{ selectedNotesFilter }}</code>
      </v-col>
    </v-row>
  </basic-dialog>
</template>

<script>
import gql from "graphql-tag";
import BasicDialog from "../../shared/BasicDialog.vue";
import { removeGQLTypename, uniqueByProp } from "../../../../helpers";
import DefaultListActions from "../../shared/DefaultListActions.vue";
import SupplierItiNoteEditDialog from "./SupplierItiNoteEditDialog.vue";
import { readonly } from "vue";
const LIST_QUERY = gql`
  query supplierItineraryNotes($itineraryId: ID!) {
    supplierItineraryNotes(itineraryId: $itineraryId) {
      edges {
        node {
          id
          itineraryId
          name
          code
          content
          supplierIds
          supplierNames
          forConfirm
          forConfirmation
          forAmendment
          forCancel
          forQuotation
          forVoucher
          forOther
          forWeb
        }
      }
    }
  }
`;
const CREATE_MUTATION = gql`
  mutation createSupplierItineraryNote($input: SupplierItineraryNoteInput!) {
    createSupplierItineraryNote(input: $input) {
      id
      name
    }
  }
`;
const UPDATE_MUTATION = gql`
  mutation updateSupplierItineraryNote($input: SupplierItineraryNoteInput!) {
    updateSupplierItineraryNote(input: $input) {
      id
      name
    }
  }
`;
const DELETE_MUTATION = gql`
  mutation deleteSupplierItineraryNote($id: ID!) {
    deleteSupplierItineraryNote(id: $id) {
      id
    }
  }
`;
export default {
  name: "supplier-iti-notes-dialog",

  components: { BasicDialog, DefaultListActions, SupplierItiNoteEditDialog },

  props: {
    show: {
      type: Boolean,
      default: () => false,
    },
    value: {
      type: Object,
      default: () => null,
    },
    itiItems: {
      type: Array,
      default: () => [],
    },
    readOnly: {
      type: Boolean,
      default: () => false,
    },
  },

  data() {
    return {
      debug: false,
      headers: [
        {
          text: "Name",
          value: "name",
          name: "name",
        },
        {
          text: "Supplier(s)",
          value: "supplierNames",
          name: "supplierNames",
        },
        {
          text: "Actions",
          value: "actions",
          name: "actions",
          align: "center",
        },
      ],
      showItemEditor: false,
      editedItem: null,
      selectedSuppliersFilter: [],
      selectedNotesFilter: [],
      notes: [],
    };
  },

  computed: {
    items() {
      let items = [];
      if (this.supplierItineraryNotes && this.supplierItineraryNotes.edges) {
        items = this.supplierItineraryNotes.edges.map(removeGQLTypename);
        if (this.selectedSuppliersFilter.length > 0) {
          this.supplierItineraryNotes.edges.map(removeGQLTypename);
          items = items.filter(
            (item) =>
              item.supplierIds.filter(
                (suppId) => this.selectedSuppliersFilter.indexOf(suppId) > -1
              ).length > 0
          );
        }
        if (this.selectedNotesFilter.length > 0) {
          items = items.filter(
            (item) => this.selectedNotesFilter.indexOf(item.code) > -1
          );
        }
        return items;
      } else {
        return items;
      }
    },
    suppliers() {
      return this.itiItems
        ? uniqueByProp(
            this.itiItems.map((item) => ({
              id: item.supplierId,
              name: item.supplierName,
            })),
            "id"
          )
        : [];
    },
  },

  methods: {
    onAddNote() {
      this.editedItem = null;
      this.showItemEditor = true;
    },
    onEditItem(payload) {
      this.editedItem = { ...payload };
      this.showItemEditor = true;
    },
    onViewItem(payload) {
      this.editedItem = { ...payload };
      this.showItemEditor = true;
    },
    onDeleteItem(payload) {
      console.log("deleting item: ", payload);
    },
    onSaveItem(payload) {
      this.showItemEditor = false;
      const mutation = payload.id ? UPDATE_MUTATION : CREATE_MUTATION;
      this.$apollo
        .mutate({
          mutation: mutation,
          variables: { input: { ...payload, itineraryId: this.value.id } },
          refetchQueries: [
            {
              query: LIST_QUERY,
              variables: { itineraryId: this.value.id },
            },
          ],
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Supplier Itinerary Note Saved.",
          });
        })
        .catch((err) => {
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
    onDeleteItem(payload) {
      this.$apollo
        .mutate({
          mutation: DELETE_MUTATION,
          variables: {
            id: payload.id,
          },
          refetchQueries: [
            {
              query: LIST_QUERY,
              variables: { itineraryId: this.value.id },
            },
          ],
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Supplier Itinerary Note Removed.",
          });
        })
        .catch((err) => {
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
  },

  apollo: {
    supplierItineraryNotes: {
      query: LIST_QUERY,
      fetchPolicy: "network-only",
      variables() {
        return { itineraryId: this.value.id };
      },
      skip() {
        return !this.value;
      },
      result({ data }) {
        this.notes = this.items
          ? uniqueByProp(
              this.items.map((item) => ({
                id: item.code,
                name: item.name,
              })),
              "id"
            )
          : [];
      },
    },
  },
};
</script>

<style>
.content {
  white-space: pre;
}
</style>



// WEBPACK FOOTER //
// src/components/gt/itineraries/supplier-notes/SupplierItiNotesDialog.vue